import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { knowledge } from '../../api/client'
import PageHeader from '../../components/PageHeader'
import { PageLayout, Card, Badge, Tag, SkeletonCard, EmptyState, IconButton } from '../../components/ui'
import { knowledgeCategories } from '../../utils/constants'
import { formatTimeAgo } from '../../utils/date'

function KnowledgeCard({ item }) {
  // Get display label from category key
  const categoryLabel = knowledgeCategories[item.category] || item.category || 'FAQ'

  return (
    <Link to={`/knowledge/${item.id}/edit`}>
      <Card hover className="block">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-indices-bright truncate">{item.title}</h3>
            {item.content && (
              <p className="text-sm text-indices-medium mt-1 line-clamp-2">{item.content}</p>
            )}
          </div>
          <Badge variant="primary" className="ml-2">
            {categoryLabel}
          </Badge>
        </div>
        {item.tags && (
          <div className="flex flex-wrap gap-1 mt-2">
            {item.tags.split(',').slice(0, 3).map((tag, i) => (
              <Badge key={i} variant="default" size="sm">
                {tag.trim()}
              </Badge>
            ))}
          </div>
        )}
        <div className="flex items-center gap-3 mt-2 text-xs text-indices-muted">
          {item.retrievalCount > 0 ? (
            <>
              <span>Used {item.retrievalCount}x</span>
              {item.lastRetrievedAt && (
                <span>· Last {formatTimeAgo(item.lastRetrievedAt)}</span>
              )}
            </>
          ) : (
            <span className="text-amber-500/70">Never used</span>
          )}
        </div>
      </Card>
    </Link>
  )
}

export default function KnowledgeListPage() {
  const [filter, setFilter] = useState('All')

  const { data: knowledgeList, isLoading } = useQuery({
    queryKey: ['knowledge'],
    queryFn: knowledge.list,
  })

  const filteredItems = knowledgeList?.filter(item =>
    filter === 'All' || item.category === filter
  ) || []

  // Build categories from constants (key for filtering, label for display)
  const categoryEntries = [
    { key: 'All', label: 'All' },
    ...Object.entries(knowledgeCategories).map(([key, label]) => ({ key, label }))
  ]

  return (
    <PageLayout>
      <PageHeader
        title="Knowledge Base"
        showBack
        backTo="/"
        actions={
          <Link to="/knowledge/new">
            <IconButton variant="ghost" label="Add knowledge">
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </IconButton>
          </Link>
        }
      />

      <div className="px-4 py-4 space-y-4">
        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2">
          {categoryEntries.map(({ key, label }) => (
            <Tag
              key={key}
              color="primary"
              selected={filter === key}
              onClick={() => setFilter(key)}
            >
              {label}
            </Tag>
          ))}
        </div>

        {/* Knowledge List */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : filteredItems.length === 0 ? (
          <EmptyState
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            }
            title="No knowledge entries found"
            message={filter !== 'All' ? `No entries in ${knowledgeCategories[filter] || filter} category` : 'Add your first entry to get started'}
            action={
              <Link to="/knowledge/new" className="text-primary-600 font-medium">
                Add your first entry
              </Link>
            }
          />
        ) : (
          <div className="space-y-3">
            {filteredItems.map(item => (
              <KnowledgeCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  )
}
